<template>
  <el-dialog
    v-model="visible"
    title="用户注册"
    width="440px"
    :close-on-click-modal="true"
    :close-on-press-escape="true"
    :show-close="true"
    custom-class="login-dialog"
    @closed="onDialogClosed"
    @opened="onDialogOpened"
  >
    <div class="login-box">
      <div class="login-header">
        <h2>简单画素材网</h2>
      </div>
      <el-form 
        ref="formRef"
        :rules="rules"
        :model="registerForm" 
        @submit.prevent="handleRegister" 
        class="login-form"
      >
        <el-form-item prop="username">
          <el-input 
            v-model="registerForm.username" 
            placeholder="请输入用户名"
            :disabled="loading"
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="password">
          <el-input 
            v-model="registerForm.password" 
            :type="passwordVisible ? 'text' : 'password'" 
            placeholder="请输入密码"
            :disabled="loading"
            @keyup.enter="handleRegister"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
            <template #suffix>
              <el-icon class="password-icon" @click="passwordVisible = !passwordVisible">
                <component :is="passwordVisible ? 'View' : 'Hide'" />
              </el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item prop="passwordAgain">
          <el-input
              v-model="registerForm.passwordAgain"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="请再次输入密码"
              :disabled="loading"
              @keyup.enter="handleRegister"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
            <template #suffix>
              <el-icon class="password-icon" @click="passwordVisible = !passwordVisible">
                <component :is="passwordVisible ? 'View' : 'Hide'" />
              </el-icon>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            :loading="loading"
            @click="handleRegister"
            class="login-button"
          >
            注册
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
// 从Element Plus获取图标
import { User, Lock, View, Hide, Warning } from '@element-plus/icons-vue'
// 引入自定义认证状态管理
import authState from '@/utils/authState.js'

const formRef = ref(null);


const backendUrl = ref(import.meta.env.VITE_JAVA_API_URL)
// 简单的加密解密函数（仅用于基本混淆，不是强安全加密）
const encryptPassword = (password) => {
  try {
    return btoa(password); // 使用Base64编码
  } catch (e) {
    console.error('加密密码失败:', e);
    return '';
  }
};


const rules = {
    username: [
      { required: true, message: '请输入用户名', trigger: 'blur' },
      { min: 2, max: 50, message: '用户名长度应为2-50个字符', trigger: 'blur' },
      { pattern: /^[A-Za-z0-9]+$/, message: '用户名只能包含英文字母和数字', trigger: 'blur' }
    ],
    password: [
      { required: true, message: '请输入密码', trigger: 'blur' },
      { min: 8, max: 20, message: '密码长度应为8-20个字符', trigger: 'blur' },
      { 
        pattern: /^(?=.*[A-Za-z])(?=.*\d)|(?=.*[A-Za-z])(?=.*[^A-Za-z0-9])|(?=.*\d)(?=.*[^A-Za-z0-9]).{8,20}$/, 
        message: '密码必须包含英文字母、数字、符号中的至少两种组合', 
        trigger: 'blur'
      }    
    ],
    passwordAgain: [
      { required: true, message: '请再次输入密码', trigger: 'blur' },
      { min: 8, max: 20, message: '密码长度应为8-20个字符', trigger: 'blur' },
      { 
        pattern: /^(?=.*[A-Za-z])(?=.*\d)|(?=.*[A-Za-z])(?=.*[^A-Za-z0-9])|(?=.*\d)(?=.*[^A-Za-z0-9]).{8,20}$/, 
        message: '密码必须包含英文字母、数字、符号中的至少两种组合', 
        trigger: 'blur'
      }
    ]
};

const decryptPassword = (encryptedPassword) => {
  try {
    return atob(encryptedPassword); // 使用Base64解码
  } catch (e) {
    console.error('解密密码失败:', e);
    return '';
  }
};

// 定义props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  redirectUrl: {
    type: String,
    default: ''
  }
})

// 定义事件
const emit = defineEmits(['update:show', 'login-success', 'login-error'])

// 本地状态
const visible = ref(false)
const router = useRouter()
const loading = ref(false)
const passwordVisible = ref(false)

// 检查是否存在已保存的凭据
const savedCredentialsExist = ref(false);

// 清除已保存的凭据
const clearSavedCredentials = () => {
  localStorage.removeItem('saved_username');
  localStorage.removeItem('saved_password');
  localStorage.removeItem('auto_login'); // 确保移除可能存在的自动注册设置
  
  registerForm.value.remember = false;
  
  // 只清除密码，保留当前输入的用户名，便于重新输入
  registerForm.value.password = '';
  
  savedCredentialsExist.value = false;
  
  ElMessage.success('已清除保存的注册信息');
};

// 监听props更新本地状态
watch(() => props.show, (newVal) => {
  console.log('LoginDialog props.show changed:', newVal)
  visible.value = newVal
  if (newVal) {
    initRegisterForm()
  }
})

// 监听本地状态更新props
watch(visible, (newVal) => {
  console.log('LoginDialog visible changed:', newVal)
  emit('update:show', newVal)
})

// 定义响应式状态
const registerForm = ref({
  username: '',
  password: '',
  passwordAgain: '',
  remember: false
})

// 初始化注册表单，尝试加载保存的用户名和密码
const initRegisterForm = () => {
  const savedUsername = localStorage.getItem('saved_username')
  const savedEncryptedPassword = localStorage.getItem('saved_password')
  
  // 检查是否存在已保存的凭据
  savedCredentialsExist.value = !!savedUsername;
  
  if (savedUsername) {
    registerForm.value.username = savedUsername
    registerForm.value.remember = true
    
    // 如果有保存的密码，也一并加载并解密
    if (savedEncryptedPassword) {
      registerForm.value.password = decryptPassword(savedEncryptedPassword)
    }
  }
}

// 打开对话框时的处理
const onDialogOpened = () => {
  console.log('LoginDialog opened')
}

// 弹窗关闭时的处理
const onDialogClosed = () => {
  console.log('LoginDialog closed')
  // 只有在未勾选记住密码时才清空密码
  if (!registerForm.value.remember) {
    registerForm.value.password = '' // 清空密码
  }
}

// 注册处理
const handleRegister = async () => {
  console.log('Attempting login with:', registerForm.value.username)
  
  // 表单验证
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
  } catch (error) {
    console.error('表单验证失败:', error);
    return;
  }
  
  // 额外验证两次密码是否一致
  if (!(registerForm.value.password === registerForm.value.passwordAgain)) {
    ElMessage.warning('两次输入的密码不一致，请验证后重试')
    return
  }

  loading.value = true
  try {
    // 首先尝试使用v1版本的API
    let response = await fetch(`${backendUrl.value}/system/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: registerForm.value.username,
        password: registerForm.value.password
      })
    })
    let data = await response.json()
    console.log('Login response:', data)
    
    // 如果v1 API返回401，尝试使用旧版API注册
    if (response.status === 401) {
      console.log('尝试降级到旧版API...')
      response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: registerForm.value.username,
          password: registerForm.value.password
        })
      })
      data = await response.json().data
      
      console.log('旧版API注册结果:', data)
    }
    
    if (!response.ok) {
      throw new Error(data.message || '注册失败')
    }
    if (data.code!=200){
      throw new Error(data.msg || '注册失败')
    }
    data = data.data
    const accessToken = data.access_token || data.accessToken;
    const refreshToken = data.refresh_token || data.refreshToken; 
    // 同时支持accessToken和token格式，以兼容新旧API
    const token = accessToken || data.token;
    if (token && data.user) {
      // 根据用户选择是否记住密码
      if (registerForm.value.remember) {
        // 如果勾选了记住密码，同时保存用户名和加密后的密码
        localStorage.setItem('saved_username', registerForm.value.username)
        localStorage.setItem('saved_password', encryptPassword(registerForm.value.password))
      } else {
        // 如果未勾选，清除保存的信息
        localStorage.removeItem('saved_username')
        localStorage.removeItem('saved_password')
      }
      
      // 使用authState注册，传入刷新令牌
      console.log('使用authState进行注册')
      authState.login(token, data.user, data.refreshToken);
      
      // 立即显示成功信息
      ElMessage.success('注册成功')
      
      // 检查是否是管理员
      const isAdmin = data.user.role === 'admin'
      console.log('用户角色:', data.user.role, '是管理员:', isAdmin)
      
      // 关闭弹窗
      visible.value = false
      
      // 发送注册成功事件
      emit('login-success', {
        user: data.user,
        isAdmin,
        redirectUrl: props.redirectUrl || (isAdmin ? '/admin' : '/')
      })
      
      // 重定向到指定页面
      if (props.redirectUrl) {
        router.push(props.redirectUrl)
      } else if (isAdmin) {
        router.push('/admin')
      }
    } else {
      throw new Error('注册返回数据格式错误')
    }
  } catch (error) {
    console.error('Login error:', error)
    ElMessage.error(error.message || '注册失败，请检查用户名和密码')
    // 发送注册失败事件
    emit('login-error', error)
  } finally {
    loading.value = false
  }
}

// 初始化
initRegisterForm()
</script>

<style scoped>
/* 自定义对话框样式 */
:deep(.login-dialog) {
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
}

:deep(.el-dialog__header) {
  padding: 15px 20px;
  margin: 0;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(90deg, #2575fc 0%, #6a11cb 100%);
}

:deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

:deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
}

:deep(.el-dialog__body) {
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.login-box {
  position: relative;
  z-index: 1;
}

.login-header {
  text-align: center;
  margin-bottom: 15px;
}

.login-header h2 {
  margin: 0;
  color: #333;
  font-size: 20px;
  font-weight: 600;
}

.login-subtitle {
  margin: 5px 0 0;
  color: #666;
  font-size: 14px;
}

.login-form {
  padding: 10px 0;
}

.password-icon {
  cursor: pointer;
  font-size: 16px;
  color: #909399;
}

.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.clear-saved-btn {
  padding: 0;
  font-size: 12px;
  color: #909399;
}

.clear-saved-btn:hover {
  color: #409EFF;
}

.security-tip {
  font-size: 12px;
  color: #E6A23C;
  text-align: left;
  margin-top: -10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.security-tip i {
  margin-right: 5px;
  font-size: 14px;
}

.login-button {
  width: 100%;
  height: 44px;
  border-radius: 22px;
  font-size: 16px;
  letter-spacing: 1px;
  background: linear-gradient(90deg, #2575fc 0%, #6a11cb 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(106, 17, 203, 0.3);
  transition: all 0.3s ease;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(106, 17, 203, 0.4);
}

/* 响应式调整 */
@media (max-width: 480px) {
  :deep(.el-dialog) {
    width: 90% !important;
    margin: 0 auto !important;
  }
}
</style>